import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ConfirmationLinkNotice } from '../components/ConfirmationLinkNotice.js';
import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Card } from '../components/ui.js';
import { formatCredits, formatDate } from '../lib/format.js';
import { statusLabel, statusToneClasses, orderTypeLabel } from '../lib/orderDisplay.js';
import {
  fetchAgencyRenewals,
  fetchOrderTypes,
  fetchOrders,
  type AgencyRenewalSite,
  type OrderListItem,
} from '../lib/ordersApi.js';
import { useSession } from '../session/SessionProvider.js';

import { BUY_PARAM } from './CreditsPage.js';

/**
 * What a site build costs is **not** written down here.
 *
 * It used to be: `const CHEAPEST_ORDER_CREDITS = 150`, a second copy of a
 * number the server already owns. D-9 divided every credit quantity by ten and
 * this constant would have gone on saying 150 — the dashboard would have told
 * an agency with 20 credits that it needed 150 for a build that now costs 15,
 * and refused to let them order something they could comfortably afford. It is
 * read from `/api/order-types` instead, where it comes from the same engine
 * that prices the order. See `NeedsCreditsCard`.
 */

/** Orders that are still work in progress — what the strip is for. */
const OPEN_STATUSES = ['submitted', 'in_build', 'in_review'] as const;

/**
 * "Sites expiring soon" — the agency's own renewals card.
 *
 * Silent when nothing is coming up: a card that says "nothing to worry about"
 * on every dashboard forever is a card people stop reading.
 */
function ExpiringSites(): React.JSX.Element | null {
  const { session } = useSession();
  const [sites, setSites] = useState<AgencyRenewalSite[]>([]);
  const [creditsPerYear, setCreditsPerYear] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [renewals, types] = await Promise.all([fetchAgencyRenewals(), fetchOrderTypes()]);
        if (cancelled) return;
        setSites(renewals.sites);
        const extension = types.orderTypes.find((type) => type.type === 'year_extension');
        if (extension) setCreditsPerYear(extension.creditsPerYear);
      } catch {
        // A dashboard card must never be the reason a dashboard fails to load.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (sites.length === 0) return null;

  /**
   * What is missing to keep every listed site live for another year.
   *
   * The deep link carries this to the buy page so the amount is already
   * entered — the buyer should not have to do this arithmetic themselves. The
   * server clamps and re-prices whatever arrives, so this is a convenience,
   * never a figure anyone is held to.
   */
  const needed = creditsPerYear === null ? 0 : sites.length * creditsPerYear;
  const available = session?.credits?.availableCredits ?? 0;
  const shortfall = Math.max(0, needed - available);

  return (
    <Card title="Sites expiring soon">
      <ul className="mt-4 divide-y divide-rule-soft">
        {sites.map((site) => (
          <li key={site.siteId} className="flex items-center gap-4 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-fg">{site.businessName}</p>
              <p className="text-fg-muted">
                {site.daysRemaining < 0
                  ? `Ended ${formatDate(site.liveUntil)}`
                  : `Ends ${formatDate(site.liveUntil)} · ${site.daysRemaining} ${
                      site.daysRemaining === 1 ? 'day' : 'days'
                    } left`}
              </p>
            </div>
            {/* Renewal is site-first now (C-1), so this goes to the sites page
                rather than into the order form's client picker. */}
            <Link to="/sites" className="shrink-0 text-sm font-medium text-fg underline">
              Keep it live
            </Link>
          </li>
        ))}
      </ul>

      {shortfall > 0 ? (
        <p className="mt-4 border-t border-rule-soft pt-4 text-sm text-fg-muted">
          You are {formatCredits(shortfall)} short of keeping{' '}
          {sites.length === 1 ? 'this site' : 'these sites'} live for another year.{' '}
          {/* The amount is in the link text as well as the URL: what the buy
              page will open with should be obvious before it is clicked. */}
          <Link
            to={`/credits?${BUY_PARAM}=${String(shortfall)}`}
            className="font-medium text-fg underline"
          >
            Buy {formatCredits(shortfall)}
          </Link>
        </p>
      ) : null}
    </Card>
  );
}

/**
 * The orders currently in flight.
 *
 * Silent when there are none, for the same reason as the card above — but the
 * primary call to action never is, so the page is never empty.
 */
function OpenOrders(): React.JSX.Element | null {
  const [orders, setOrders] = useState<OrderListItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchOrders();
        if (cancelled) return;
        setOrders(
          response.orders.filter((order) =>
            (OPEN_STATUSES as readonly string[]).includes(order.status),
          ),
        );
      } catch {
        // As above: a strip that cannot load must not take the page with it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (orders.length === 0) return null;

  return (
    <Card className="p-0">
      <h2 className="px-6 pt-6 pb-2 text-sm font-semibold text-fg-muted">Open orders</h2>
      <ul className="divide-y divide-rule-soft border-t border-rule-soft">
        {orders.map((order) => (
          <li key={order.id} className="flex items-center gap-4 px-6 py-4">
            <div className="min-w-0 flex-1">
              <Link
                to={`/orders/${order.id}`}
                className="text-sm font-medium text-fg underline"
              >
                {order.businessName}
              </Link>
              <p className="mt-0.5 text-sm text-fg-muted">
                {orderTypeLabel(order.orderType)} · {formatDate(order.createdAt)}
              </p>
            </div>
            <span
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusToneClasses(
                order.status,
              )}`}
            >
              {statusLabel(order.status)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * "You need credits before you can order" — ruling A-3.
 *
 * Prominent only when buying credits is the thing standing between the agency
 * and an order; otherwise it is one more button competing with the one that
 * matters, and "Buy credits" stays on the Credits page where it belongs.
 *
 * Renders nothing until the order types have arrived. A card that guessed the
 * threshold would flash the wrong advice on every load, and guessing is what
 * the hard-coded constant this replaced was doing permanently.
 */
function NeedsCreditsCard({
  available,
  suspended,
}: {
  available: number;
  suspended: boolean;
}): React.JSX.Element | null {
  const [siteBuildCredits, setSiteBuildCredits] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { orderTypes } = await fetchOrderTypes();
        const build = orderTypes.find((type) => type.type === 'site_build');
        if (!cancelled && build) setSiteBuildCredits(build.credits);
      } catch {
        // The dashboard is still useful without this card, and an agency that
        // cannot reach the API has a larger problem than a missing nudge.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (siteBuildCredits === null || available >= siteBuildCredits) return null;

  return (
    <Card>
      <p className="text-sm font-medium text-fg">You need credits before you can order</p>
      <p className="mt-1 text-sm text-fg-muted">
        A site build costs {formatCredits(siteBuildCredits)} and you have {formatCredits(available)}{' '}
        available.
      </p>
      {/*
        WAIT-B defect 5. P-4 took the buy controls off every other screen and
        missed this one, so a suspended account was still being offered "Buy
        credits" on its own dashboard - and the button led to the page that
        refuses. The card itself stays: "you need credits before you can order"
        is still true, and it is part of why the account is stuck.

        **`RefusalNotice`, not `SuspendedNotice`** - WAIT-B round 2. This was
        the one place still reaching past the chooser for the suspended line
        directly, so an operator viewing a suspended agency was told "you
        cannot buy credits while your account is suspended" about an account
        that is not theirs. It broke the viewing-wins rule from inside the
        component that exists to keep it.
      */}
      {suspended ? (
        <div className="mt-4">
          <RefusalNotice action="buy credits" />
        </div>
      ) : (
        <Link
          to="/credits"
          className="mt-4 inline-flex items-center justify-center rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
        >
          Buy credits
        </Link>
      )}
    </Card>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { session } = useSession();
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();

  if (!session) return <Alert>We could not load your account.</Alert>;

  if (session.user.role === 'admin') {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Operator</h1>
        <p className="text-sm text-fg-muted">Everything you need is in the navigation above.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <p className="text-sm font-medium text-fg">Order queue</p>
            <p className="mt-1 text-sm text-fg-muted">Open work, and what to do next with it.</p>
            <Link to="/admin/orders" className="mt-3 inline-block text-sm underline">
              Open the queue
            </Link>
          </Card>
          <Card>
            <p className="text-sm font-medium text-fg">Reconciliation</p>
            <p className="mt-1 text-sm text-fg-muted">Anything that needed a human.</p>
            <Link to="/admin/reconciliation" className="mt-3 inline-block text-sm underline">
              Check reconciliation
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const credits = session.credits;
  const available = credits?.availableCredits ?? 0;

  return (
    <div className="space-y-8">
      {/* C-124. What a confirmation link just did, once, above everything. */}
      <ConfirmationLinkNotice />

      {/* The order comes first — the whole point of section A. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {session.agency?.name ?? 'Your agency'}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            Order a site build, and we take it from there.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/credits"
            className="inline-flex items-center gap-2 rounded-full bg-sunken px-3 py-1.5 text-sm font-medium text-fg-2 transition hover:bg-rule"
          >
            {formatCredits(available)} available
            {(credits?.reservedCredits ?? 0) > 0 ? (
              <span className="text-fg-muted">
                · {formatCredits(credits?.reservedCredits ?? 0)} held
              </span>
            ) : null}
          </Link>
          {suspended ? null : (
          <Link
            to="/orders/new"
            className="inline-flex items-center justify-center rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
          >
            Place an order
          </Link>
          )}
        </div>
      </div>

      {!session.user.emailVerified ? (
        <Alert tone="info">
          We have sent a confirmation link to {session.user.email}. You can carry on in the
          meantime.
        </Alert>
      ) : null}

      <NeedsCreditsCard available={available} suspended={suspended} />

      <OpenOrders />

      <ExpiringSites />
    </div>
  );
}
